import { useState } from "react";
import { api, setOperator, errToast } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Identificação por PIN.
 *
 * Dois usos, um componente:
 *  - início de turno (`mode="shift"`): quem digitar passa a ser o operador do
 *    aparelho, e toda gravação sai no nome dele até trocar ou expirar;
 *  - autorização pontual (`mode="authorize"`): o gerente libera UMA ação e o
 *    turno de quem está no balcão não muda. Quem chama recebe o token em
 *    `onAuthorized` e executa a ação com `runAsOperator`.
 *
 * O PIN nunca é credencial de acesso: só troca a identidade dentro de uma
 * sessão já autenticada do mesmo negócio.
 */
export default function OperatorPinDialog({
  open,
  onOpenChange,
  mode = "shift",
  action = "",
  requireManager = false,
  onIdentified,
  onAuthorized,
}) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState("");

  const fechar = (v) => {
    if (!v) { setPin(""); setErro(""); }
    onOpenChange(v);
  };

  const confirmar = async (e) => {
    e?.preventDefault?.();
    if (pin.length < 4) { setErro("O PIN tem de 4 a 6 dígitos."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/operators/identify", { pin });
      if (requireManager && !["owner", "manager"].includes(data.operator.role)) {
        setErro(`${data.operator.name} não é gerente nem dono.`);
        setPin("");
        return;
      }
      setPin("");
      setErro("");
      if (mode === "authorize") {
        onAuthorized?.(data.operator_token, data.operator);
      } else {
        setOperator(data.operator_token, data.operator.name);
        onIdentified?.(data.operator);
      }
      onOpenChange(false);
    } catch (err) {
      setPin("");
      const detail = err?.response?.data?.detail;
      setErro(String(detail?.message || detail || "Não foi possível identificar."));
      if (err?.response?.status >= 500) errToast(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={fechar}>
      <DialogContent className="sm:max-w-sm" data-testid="operator-pin-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === "authorize" ? "Autorização do gerente" : "Quem está operando?"}
          </DialogTitle>
          <DialogDescription>
            {action ||
              (mode === "authorize"
                ? "Peça ao gerente para digitar o PIN dele."
                : "Digite seu PIN. Cada venda, sangria e comanda passa a sair no seu nome.")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={confirmar} className="space-y-3">
          <Input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
            value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, "")); setErro(""); }}
            placeholder="PIN"
            className="text-center text-2xl tracking-[0.5em]"
            autoFocus
            data-testid="operator-pin-input"
          />
          {erro && <p className="text-sm text-destructive" data-testid="operator-pin-error">{erro}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => fechar(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button type="submit" disabled={busy || pin.length < 4} data-testid="operator-pin-submit">
              {busy ? "…" : mode === "authorize" ? "Autorizar" : "Entrar no turno"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
